import { SubAccountStates } from '@generated/graphql';

const NON_TRADEABLE_STATES: readonly SubAccountStates[] = [
  SubAccountStates.Inactive,
  SubAccountStates.AwaitingApproval,
];

type TradeableAllocationMember = {
  isTradeable?: boolean | null;
  state?: SubAccountStates;
};

type TradeableAllocationMemberReport = {
  isTradeable?: boolean | null;
  subAccount?: { state?: SubAccountStates };
};

// Mirrors holding-service `isTradeableMember`, which setCustomExpectedPercentages
// uses to pick the members it validates and persists. Missing state is tradeable.
export const isTradeableAllocationState = (
  state: SubAccountStates | undefined,
) => state === undefined || !NON_TRADEABLE_STATES.includes(state);

// Same conjunction as the backend: `isTradeable` also covers a closed account,
// user or goal, but a stale stamp must not override an inactive sub-account
// state. `isTradeable` is null on older reports, which rely on state alone.
export const isTradeableAllocationMember = ({
  isTradeable,
  state,
}: TradeableAllocationMember) =>
  isTradeable !== false && isTradeableAllocationState(state);

export const isTradeableAllocationReportMember = (
  subAccountReport: TradeableAllocationMemberReport,
) =>
  isTradeableAllocationMember({
    isTradeable: subAccountReport.isTradeable,
    state: subAccountReport.subAccount?.state,
  });

export const firstTradeableSubAccount = <T extends TradeableAllocationMember>(
  subAccounts: T[],
) => subAccounts.find((subAccount) => isTradeableAllocationMember(subAccount));

export const tradeableAllocationSubAccounts = <
  T extends TradeableAllocationMemberReport,
>(
  subAccounts: T[] | undefined,
) => (subAccounts ?? []).filter(isTradeableAllocationReportMember);
